import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";

export const useFetchPerson = () => {
  const { fetchData } = useApi();
  const { organizationId, sport, dataId } = useParams();

  return useQuery({
    queryKey: ["persons", organizationId, dataId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/persons/${dataId}`,
      });
      return result?.data?.[0];
    },
    enabled: !!dataId,
  });
};

export const useFetchPersonsPagination = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/persons`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["persons", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useFetchPersons = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/persons`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["persons", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data || [];
    },
  });
};

export const useFetchSeasonPersons = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  let URL = `${sport}/o/${organizationId}/seasons/${seasonId}/persons`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["persons", organizationId, seasonId, JSON.stringify(params)],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data || [];
    },
  });
};

export const useDeletePerson = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (personId) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/persons/${personId}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "person.delete.success",
          defaultMessage: "Person deleted successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["persons"] });
    },
  });
};

export const useCreatePerson = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();

  return useMutation({
    mutationFn: async (data) => {
      return await mutateData({
        url: `${sport}/o/${organizationId}/persons`,
        method: "POST",
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["persons"] });
    },
  });
};

export const useUpdatePerson = () => {
  const { sport, organizationId, dataId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      return await mutateData({
        url: `${sport}/o/${organizationId}/persons/${dataId}`,
        method: "PUT",
        data: data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "person.update.success",
          defaultMessage: "Person updated successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["persons"] });
    },
  });
};
